import { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Mail,
  Phone,
  Wifi,
  Send,
  CheckCircle2,
  MessageCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useSite } from '../lib/SiteContentContext';
import { submitEnquiry } from '../lib/api';

const COURSE_OPTIONS = [
  'GCSE Science',
  'A Level Biology',
  '1:1 tuition',
  'Not sure yet',
];

const YEAR_GROUPS = ['Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13'];

const EXAM_BOARDS = ['AQA', 'OCR', 'Other'];

/**
 * Contact form:
 *   - Required client-side validation
 *   - Honeypot field for basic spam protection
 *   - Minimum-time-to-submit heuristic (bots submit instantly)
 *   - Consent statement referring to Privacy Policy
 *   - Submits to /api/enquiries which stores the enquiry server-side.
 */
export default function Contact() {
  const { brand } = useSite();
  // Prefill the course dropdown when a visitor arrives from the
  // "Enquire about 1:1 tuition" CTA on the Courses page.
  const { search } = useLocation();
  const prefillCourse = useMemo(() => {
    const interest = new URLSearchParams(search).get('interest');
    return interest === '1-to-1' ? '1:1 tuition' : '';
  }, [search]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    course: prefillCourse,
    yearGroup: '',
    examBoard: '',
    consent: false,
    honeypot: '',
  });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const startedAt = useMemo(() => Date.now(), []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    if (!form.email.trim()) e.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = 'Please enter a valid email address.';
    if (form.phone && !/^[0-9+()\s-]{7,}$/.test(form.phone.trim()))
      e.phone = 'Please enter a valid phone number.';
    if (!form.course) e.course = 'Please choose a course.';
    if (!form.yearGroup) e.yearGroup = 'Please choose a year group.';
    if (!form.examBoard) e.examBoard = 'Please choose an exam board.';
    if (!form.consent) e.consent = 'Please agree to the privacy statement to continue.';
    return e;
  };

  const buildMailto = () => {
    // Fallback: user can email us directly if the API is unreachable.
    return `mailto:${brand?.email || 'info@sciencesorted.co.uk'}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Basic spam protection
    if (form.honeypot) return;
    if (Date.now() - startedAt < 1500) return;

    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) {
      const firstKey = Object.keys(v)[0];
      const el = document.getElementById(firstKey);
      if (el) el.focus();
      return;
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      await submitEnquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        course: form.course,
        yearGroup: form.yearGroup,
        examBoard: form.examBoard,
        consent: form.consent,
        website: form.honeypot,
      });
      // Only clear the form after a genuine success.
      setForm({
        name: '',
        email: '',
        phone: '',
        course: '',
        yearGroup: '',
        examBoard: '',
        consent: false,
        honeypot: '',
      });
      setSent(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        'Something went wrong while sending your enquiry. Please try again or contact us at info@sciencesorted.co.uk.';
      setSubmitError(typeof msg === 'string' ? msg : 'Something went wrong while sending your enquiry. Please try again or contact us at info@sciencesorted.co.uk.');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (key) =>
    errors[key] ? (
      <p
        id={`${key}-error`}
        className="mt-1.5 text-sm text-red-600 flex items-center gap-1"
      >
        <AlertCircle className="w-4 h-4" aria-hidden="true" /> {errors[key]}
      </p>
    ) : null;

  return (
    <main>
      <section className="pt-10 md:pt-14 pb-6 md:pb-8 bg-gradient-to-b from-brand-mint/40 to-brand-cream">
        <div className="container text-center max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-ink/10 px-3.5 py-1.5 text-sm text-brand-ink">
            <Send className="w-4 h-4 text-brand-teal" aria-hidden="true" /> Get in touch
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl text-brand-ink">
            Get in touch.
          </h1>
          <p className="mt-3 text-brand-ink/70 text-base md:text-lg">
            We respond within 24 hours.
          </p>
        </div>
      </section>

      <section className="pt-6 pb-16 md:pb-20 container">
        <div className="grid lg:grid-cols-5 gap-6 md:gap-10">
          {/* FORM */}
          <div className="lg:col-span-3 bg-white rounded-3xl border border-brand-ink/10 p-6 md:p-8 lg:p-10">
            {sent ? (
              <div className="text-center py-10 md:py-14">
                <div className="w-16 h-16 rounded-full bg-brand-mint grid place-items-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-brand-teal" aria-hidden="true" />
                </div>
                <h2 className="mt-5 font-display text-2xl md:text-3xl text-brand-ink">
                  Thanks - we&rsquo;ve received your enquiry and will be in touch within 24 hours.
                </h2>
                <p className="mt-2 text-brand-ink/70 max-w-md mx-auto">
                  We&rsquo;ve also sent you a confirmation email. If you need us sooner, email us directly at{' '}
                  <a className="underline" href={`mailto:${brand?.email || 'info@sciencesorted.co.uk'}`}>
                    {brand?.email || 'info@sciencesorted.co.uk'}
                  </a>
                  .
                </p>
                <Button
                  onClick={() => setSent(false)}
                  variant="outline"
                  className="mt-6 rounded-full"
                >
                  Send another
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate aria-describedby="form-privacy">
                {/* Honeypot */}
                <div
                  className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden"
                  aria-hidden="true"
                >
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.honeypot}
                    onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
                  />
                </div>

                {/* Name + Email */}
                <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                  <div>
                    <Label htmlFor="name">
                      Name{' '}
                      <span aria-hidden="true" className="text-red-600">
                        *
                      </span>
                    </Label>
                    <Input
                      id="name"
                      required
                      autoComplete="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-2"
                      placeholder="Jane Smith"
                      data-testid="contact-name"
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                    />
                    {fieldError('name')}
                  </div>
                  <div>
                    <Label htmlFor="email">
                      Email address{' '}
                      <span aria-hidden="true" className="text-red-600">
                        *
                      </span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-2"
                      placeholder="you@example.com"
                      data-testid="contact-email"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    {fieldError('email')}
                  </div>
                </div>

                {/* Phone */}
                <div className="mt-4 md:mt-5">
                  <Label htmlFor="phone">Phone number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2"
                    placeholder="07123 456 789"
                    data-testid="contact-phone"
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                  />
                  {fieldError('phone')}
                </div>

                {/* Course */}
                <div className="mt-4 md:mt-5">
                  <Label htmlFor="course">
                    Course of interest{' '}
                    <span aria-hidden="true" className="text-red-600">
                      *
                    </span>
                  </Label>
                  <Select
                    value={form.course}
                    onValueChange={(v) => setForm({ ...form, course: v })}
                  >
                    <SelectTrigger id="course" className="mt-2" data-testid="contact-course">
                      <SelectValue placeholder="Choose a course" />
                    </SelectTrigger>
                    <SelectContent>
                      {COURSE_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldError('course')}
                </div>

                {/* Year group + Exam board */}
                <div className="grid md:grid-cols-2 gap-4 md:gap-5 mt-4 md:mt-5">
                  <div>
                    <Label htmlFor="yearGroup">
                      Student&rsquo;s year group{' '}
                      <span aria-hidden="true" className="text-red-600">
                        *
                      </span>
                    </Label>
                    <Select
                      value={form.yearGroup}
                      onValueChange={(v) => setForm({ ...form, yearGroup: v })}
                    >
                      <SelectTrigger
                        id="yearGroup"
                        className="mt-2"
                        data-testid="contact-year-group"
                      >
                        <SelectValue placeholder="Choose a year group" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEAR_GROUPS.map((y) => (
                          <SelectItem key={y} value={y}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldError('yearGroup')}
                  </div>
                  <div>
                    <Label htmlFor="examBoard">
                      Exam board{' '}
                      <span aria-hidden="true" className="text-red-600">
                        *
                      </span>
                    </Label>
                    <Select
                      value={form.examBoard}
                      onValueChange={(v) => setForm({ ...form, examBoard: v })}
                    >
                      <SelectTrigger
                        id="examBoard"
                        className="mt-2"
                        data-testid="contact-exam-board"
                      >
                        <SelectValue placeholder="Choose an exam board" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXAM_BOARDS.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldError('examBoard')}
                  </div>
                </div>

                {/* Consent */}
                <div className="mt-5 flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={form.consent}
                    onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
                    aria-invalid={!!errors.consent}
                    aria-describedby={errors.consent ? 'consent-error' : 'form-privacy'}
                    className="mt-1"
                    data-testid="contact-consent"
                  />
                  <div className="text-sm text-brand-ink/80">
                    <label htmlFor="consent" className="cursor-pointer">
                      I agree to Science Sorted contacting me about my enquiry and I have read the{' '}
                      <a href="/privacy" className="underline hover:text-brand-teal">
                        Privacy Policy
                      </a>
                      .{' '}
                      <span aria-hidden="true" className="text-red-600">
                        *
                      </span>
                    </label>
                    <p id="form-privacy" className="mt-1 text-xs text-brand-ink/60">
                      Your details are only used to respond to this enquiry. We never sell your
                      data.
                    </p>
                    {fieldError('consent')}
                  </div>
                </div>

                {submitError && (
                  <div
                    role="alert"
                    className="mt-5 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-700"
                    data-testid="contact-submit-error"
                  >
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{submitError}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="mt-6 bg-brand-ink text-brand-cream hover:bg-brand-teal rounded-full h-12 px-6 gap-2 w-full sm:w-auto disabled:opacity-70"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      Sending&hellip; <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    </>
                  ) : (
                    <>
                      Send enquiry <Send className="w-4 h-4" aria-hidden="true" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>

          {/* CONTACT CARDS */}
          <div className="lg:col-span-2 space-y-3 md:space-y-4">
            <a
              href={`mailto:${brand.email}`}
              className="flex items-start gap-4 p-5 md:p-6 bg-white rounded-3xl border border-brand-ink/10 hover:bg-brand-butter/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink"
              data-testid="contact-card-email"
            >
              <div className="w-11 h-11 rounded-2xl bg-brand-mint grid place-items-center">
                <Mail className="w-5 h-5 text-brand-teal" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-ink/50 font-semibold">
                  Email
                </div>
                <div className="mt-1 font-display text-lg md:text-xl text-brand-ink break-all">
                  {brand.email}
                </div>
              </div>
            </a>

            <a
              href={`tel:${brand.phone.replace(/\s/g, '')}`}
              className="flex items-start gap-4 p-5 md:p-6 bg-white rounded-3xl border border-brand-ink/10 hover:bg-brand-butter/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink"
              data-testid="contact-card-phone"
            >
              <div className="w-11 h-11 rounded-2xl bg-brand-mint grid place-items-center">
                <Phone className="w-5 h-5 text-brand-teal" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-ink/50 font-semibold">
                  Phone
                </div>
                <div className="mt-1 font-display text-lg md:text-xl text-brand-ink">
                  {brand.phoneDisplay}
                </div>
              </div>
            </a>

            <a
              href={brand.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-5 md:p-6 bg-white rounded-3xl border border-brand-ink/10 hover:bg-brand-butter/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink"
              data-testid="contact-card-whatsapp"
            >
              <div className="w-11 h-11 rounded-2xl bg-[#25D366] grid place-items-center">
                <MessageCircle className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-ink/50 font-semibold">
                  WhatsApp
                </div>
                <div className="mt-1 font-display text-lg md:text-xl text-brand-ink">
                  Chat on WhatsApp
                </div>
                <div className="text-sm text-brand-ink/60">Fastest way to reach us</div>
              </div>
            </a>

            <div className="flex items-start gap-4 p-5 md:p-6 bg-white rounded-3xl border border-brand-ink/10">
              <div className="w-11 h-11 rounded-2xl bg-brand-mint grid place-items-center">
                <Wifi className="w-5 h-5 text-brand-teal" aria-hidden="true" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-ink/50 font-semibold">
                  Online tuition
                </div>
                <div className="mt-1 font-display text-lg md:text-xl text-brand-ink">
                  Available to students across the UK
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
